import {UserModel} from 'app/modules/auth'
import clsx from 'clsx'
import {
  hasPermission,
  isAdmin,
  // isLocalHost, — only used by the hidden Onboarding Wizard switch (see below)
  isSelectiveAiDomains,
  shouldShowAiCredits,
} from 'common/HelperFunctions'
import Avatar from 'common/components/Avatar'
import HomeworkBetaToggle from 'common/components/BetaToggle/Homework'
import HelpPopup from 'common/components/HelpPopup'
import Spinner from 'common/components/Spinner'
import useSupportEntry from 'common/components/SupportChoice/useSupportEntry'
import {isHomeworkBetaEnabled} from 'common/helpers'
import {openAiCreditsUsage, openPlanUpgradeSelection} from 'common/helpers/openPlanUpgrade'
import {AiCreditType} from 'common/hooks/useAiCredits'
import {useMediaQuery} from 'common/hooks/useMediaQuery'
import {HeaderMessagesMenu, HeaderNotificationsMenu, HeaderUserMenu} from 'common/partials'
import {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {Link} from 'react-router'
import {RootState} from 'setup'

// Only used by the hidden Onboarding Wizard switch (see below):
// import {getUserPreferences, updateUserPreferences} from 'setup/redux/Actions/Dashboard/dashboard'

import {MenuComponent} from '_metronic/assets/ts/components'
import {KTIcon} from '_metronic/helpers'
import {ThemeModeSwitcher} from '_metronic/partials'

import StudentHouseBadge from './StudentHouseBadge'
import {DESKTOP_MEDIA_QUERY} from './breakpoints'

const itemClass = 'ms-1 ms-md-4'
/** Items that only fit the header from `lg` up; below that HeaderUserMenu shows them. */
const lgUpItemClass = 'd-none d-lg-flex ms-md-4'
const btnClass =
  'btn btn-icon btn-custom btn-icon-muted btn-active-light btn-active-color-primary w-35px h-35px'
const btnIconClass = 'fs-2'

const Navbar = () => {
  const dispatch = useDispatch()
  const intl = useIntl()
  // From `lg` up HeaderWrapper shows the house badge at the start of the header.
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY)

  useEffect(() => {
    MenuComponent.reinitialization()
  }, [])

  // "Need Any Support?" asks Versi-or-ticket, as SupportTicketsLink does on the ERP domains; the
  // href stays as the fallback when the chatbot is off.
  const {onSupportClick, supportChoiceModal} = useSupportEntry()
  const [showComingSoonModal, setShowComingSoonModal] = useState(false)
  const [isViewingAiCreditsUsage, setIsViewingAiCreditsUsage] = useState(false)
  const [isBuyingAiCredits, setIsBuyingAiCredits] = useState(false)

  const user: UserModel = useSelector<RootState>(
    ({userReducer}) => userReducer.user,
    shallowEqual
  ) as UserModel
  const impersonation: any = useSelector<RootState>(
    ({userReducer}) => userReducer.impersonation,
    shallowEqual
  )

  const notificationsCount = useSelector<RootState>(
    ({notificationsReducer}) => notificationsReducer.notificationsCount,
    shallowEqual
  ) as number

  const aiCredits = useSelector<RootState>(
    ({userReducer}) => userReducer.aiCredits,
    shallowEqual
  ) as AiCreditType | null

  /* Onboarding Wizard flip switch — HIDDEN: we are working on the new
     onboarding; until then this switch is hidden from all views (web +
     mobile). This is the state/handler for the commented-out switch in the
     JSX below — restore both together.

  const userPreferences = useSelector<RootState>(
    ({mainReducer}) => mainReducer.userPreferences,
    shallowEqual
  ) as any

  const isOnboardingDisabled = userPreferences?.OnboardingWizard?.tutorial?.dashboard === false

  const [togglingOnboarding, setTogglingOnboarding] = useState(false)

  const handleToggleOnboarding = () => {
    setTogglingOnboarding(true)
    const newValue = !isOnboardingDisabled
    const body = {
      preferences: {
        ...userPreferences,
        OnboardingWizard: {
          ...(userPreferences?.OnboardingWizard ?? {}),
          tutorial: {
            ...(userPreferences?.OnboardingWizard?.tutorial ?? {}),
            dashboard: newValue ? false : true,
          },
        },
      },
    }
    dispatch(
      updateUserPreferences(
        body,
        () => {
          dispatch(
            getUserPreferences(
              () => {},
              () => {}
            )
          )
          setTogglingOnboarding(false)
        },
        () => setTogglingOnboarding(false)
      )
    )
  }
  */

  // Same usage view as the sidebar "View Details" and the phone profile-menu row.
  const handleViewAiCreditsUsage = () => {
    if (isViewingAiCreditsUsage) return
    openAiCreditsUsage(dispatch, {
      onStart: () => setIsViewingAiCreditsUsage(true),
      onComplete: () => setIsViewingAiCreditsUsage(false),
      onError: (error) => console.error('Failed to open AI credits usage:', error),
    })
  }

  // Same plan-selection flow as the sidebar "Buy AI Credits" button.
  const handleBuyAiCredits = () => {
    if (isBuyingAiCredits) return
    openPlanUpgradeSelection(dispatch, {
      onStart: () => setIsBuyingAiCredits(true),
      onComplete: () => setIsBuyingAiCredits(false),
      onError: (error) => console.error('Failed to open Buy AI Credits:', error),
    })
  }

  return (
    <>
      {/* AI Credits Modal */}
      <Modal show={showComingSoonModal} onHide={() => setShowComingSoonModal(false)} centered>
        <Modal.Header closeButton className='border-0 pb-0'>
          <Modal.Title className='d-flex align-items-center gap-2'>
            <i className='fa-solid fa-coins text-warning fs-1' />
            <span>{intl.formatMessage({id: 'SCHEDULE.TIMETABLE.VIEW_AI_CREDITS_USAGE'})}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='text-center py-8'>
          <div className='d-flex flex-column align-items-center gap-3 mb-5'>
            <div className='d-flex align-items-center justify-content-center bg-light-warning rounded-circle w-60px h-60px'>
              <i className='fa-solid fa-coins text-warning fs-2x' />
            </div>
            <h3 className='mb-0 fw-bold text-gray-800'>
              {aiCredits?.remaining_credits !== null && aiCredits?.remaining_credits !== undefined
                ? aiCredits.remaining_credits.toLocaleString()
                : '0'}
            </h3>
            <span className='text-muted fs-6'>
              {intl.formatMessage({id: 'SCHEDULE.TIMETABLE.AI_CREDITS_REMAINING'})}
            </span>
          </div>
          <p className='text-muted mb-0 fs-7'>
            {intl.formatMessage({id: 'SCHEDULE.TIMETABLE.AI_CREDITS_TOP_UP_HINT'})}
          </p>
        </Modal.Body>
        <Modal.Footer className='border-0 pt-0 d-flex justify-content-between align-items-center'>
          <button
            onClick={handleViewAiCreditsUsage}
            className='btn btn-sm btn-light-primary fw-bold'
            disabled={isViewingAiCreditsUsage}
          >
            {isViewingAiCreditsUsage ? (
              <Spinner size='sm' type='border' className='me-2' />
            ) : (
              <KTIcon iconName='chart-simple' className='fs-4 me-1' />
            )}
            {intl.formatMessage({id: 'SCHEDULE.TIMETABLE.VIEW_AI_CREDITS_USAGE'})}
          </button>
          <button
            type='button'
            className='btn btn-sm btn-primary fw-bold'
            onClick={handleBuyAiCredits}
            disabled={isBuyingAiCredits}
          >
            {isBuyingAiCredits ? (
              <Spinner size='sm' type='border' className='me-2' />
            ) : (
              <i className='fa-solid fa-coins fs-5 me-2' aria-hidden />
            )}
            {intl.formatMessage({id: 'MENU.BUY_AI_CREDITS'})}
          </button>
        </Modal.Footer>
      </Modal>

      <div className='app-navbar flex-shrink-0'>
        {/* <div className={clsx('app-navbar-item align-items-stretch', itemClass)}>
        <Search />
      </div> */}

        {/* <div className={clsx('app-navbar-item', itemClass)}>
        <div id='kt_activities_toggle' className={btnClass}>
          <KTIcon iconName='chart-simple' className={btnIconClass} />
        </div>
      </div> */}

        {/* Below `lg` the student's house badge sits here, first of the
            right-hand items (from `lg` up HeaderWrapper puts it at the start
            of the header). Renders nothing unless the school's General
            Settings switch is on and the student is in a house. */}
        {!isDesktop && <StudentHouseBadge className={itemClass} />}

        {/* Page-level controls that used to sit in the (now dropped) toolbar
            row. Each renders nothing on routes it does not apply to, so the
            wrapper class is passed in rather than wrapped here. Below `lg`
            they move into the profile menu (HeaderUserMenu) — a 320px header
            only fits the bell and avatar beside the logo. */}
        {!isSelectiveAiDomains() && (
          <>
            {isHomeworkBetaEnabled() && (
              <HomeworkBetaToggle className={clsx('app-navbar-item', lgUpItemClass)} />
            )}
            <HelpPopup variant='icon' className={clsx('app-navbar-item', lgUpItemClass)} />
          </>
        )}

        {/* NOTIFICATIONS - hidden for isSelectiveAiDomains */}
        {!isSelectiveAiDomains() && (
          <div className={clsx('app-navbar-item', itemClass)}>
            <div
              data-kt-menu-trigger='click'
              data-kt-menu-attach='parent'
              data-kt-menu-placement='bottom-end'
              className={clsx('position-relative', btnClass)}
            >
              {notificationsCount > 0 && (
                <span className='badge badge-sm badge-circle badge-light-success position-absolute top-0 end-0'>
                  {notificationsCount > 99 ? '99+' : notificationsCount}
                </span>
              )}
              <i className='fa fa-bell' style={{fontSize: '20px'}} />
            </div>
            <HeaderNotificationsMenu />
          </div>
        )}

        {/* <div className={clsx('app-navbar-item', itemClass)}>
        <div
          data-kt-menu-trigger="{default: 'click'}"
          data-kt-menu-attach='parent'
          data-kt-menu-placement='bottom-end'
          className={btnClass}
        >
          <KTIcon iconName='element-plus' className={btnIconClass} />
        </div>
        <HeaderNotificationsMenu />
      </div> */}

        {/* Messages - hidden for isSelectiveAiDomains; in the profile menu below lg.
            Also hidden when messaging is off: `message_manager_view` folds the
            instance, branch and student-module toggles into one answer, so this
            is the same question the API's own route guard asks. */}
        {!isSelectiveAiDomains() && hasPermission('message_manager_view') && (
          <div className={clsx('app-navbar-item', lgUpItemClass)}>
            <div
              data-kt-menu-trigger='click'
              data-kt-menu-attach='parent'
              data-kt-menu-placement='bottom-end'
              className={clsx('position-relative', btnClass)}
            >
              <span
                id='chat-unread-badge'
                className='bullet bullet-dot bg-success h-6px w-6px position-absolute translate-middle top-0 start-50 animation-blink'
                style={{display: 'none'}}
              />
              <KTIcon iconName='message-text-2' className={btnIconClass} />
            </div>
            <HeaderMessagesMenu />
          </div>
        )}

        {/* GENERAL SETTINGS - hidden for isSelectiveAiDomains; in the profile menu below lg */}
        {!isSelectiveAiDomains() && hasPermission('user_manager_settings') && (
          <div className={clsx('app-navbar-item', lgUpItemClass)}>
            <Link
              to={'/institute/general-settings/settings'}
              className='btn btn-icon text-muted btn-active-light-primary btn-custom'
            >
              <i className='fa fa-gear' style={{fontSize: '20px'}} />
            </Link>
          </div>
        )}

        {/* Onboarding Wizard flip switch — HIDDEN: we are working on the new
            onboarding; until then this switch is hidden from all views
            (web + mobile). Restore by uncommenting this block and the
            matching handler/state above (isOnboardingDisabled,
            togglingOnboarding, handleToggleOnboarding).
        {isLocalHost() && isAdmin() && (
          <div className={clsx('app-navbar-item', itemClass)}>
            <label
              className='form-check form-switch form-check-custom form-check-solid d-flex align-items-center gap-2 cursor-pointer mb-0'
              title='Toggle Onboarding Wizard'
            >
              <input
                className='form-check-input'
                type='checkbox'
                checked={!isOnboardingDisabled}
                onChange={handleToggleOnboarding}
                disabled={togglingOnboarding}
              />
              <span className='form-check-label fw-semibold text-muted fs-7 d-none d-md-inline'>
                Onboarding
              </span>
            </label>
          </div>
        )}
        */}

        {/* Support Tickets - visible for isSelectiveAiDomains */}
        {isSelectiveAiDomains() && (
          <div className={clsx('app-navbar-item', itemClass)}>
            <a
              href='/institute/support-tickets'
              onClick={onSupportClick}
              className={clsx(
                'd-flex align-items-center justify-content-center px-3 py-2 rounded w-auto',
                'text-primary',
                'text-hover-primary'
              )}
              style={{whiteSpace: 'nowrap'}}
            >
              <i className='fa fa-headset me-md-2' style={{fontSize: '15px'}} />
              <span className='d-none d-md-inline'>
                {intl.formatMessage({id: 'MENU.SUPPORT_TICKETSPAGE'})}
              </span>
            </a>
            {supportChoiceModal}
          </div>
        )}

        {/* <div className={clsx('app-navbar-item', itemClass)}>
        <div className={clsx('position-relative', btnClass)} id='kt_drawer_chat_toggle'>
          <KTIcon iconName='message-text-2' className={btnIconClass} />
          <span className='bullet bullet-dot bg-success h-6px w-6px position-absolute translate-middle top-0 start-50 animation-blink' />
        </div>
      </div> */}

        {/* AI CREDITS DISPLAY - Visible only for ADMIN, hidden on mobile (shown in profile menu instead) */}
        {isAdmin() && shouldShowAiCredits() && (
          <div className={clsx('app-navbar-item d-none d-md-flex', itemClass)}>
            <div
              className='btn btn-custom btn-active-light-warning d-flex align-items-center gap-2 px-4 py-2 rounded-pill'
              onClick={() => setShowComingSoonModal(true)}
              title={intl.formatMessage({id: 'SCHEDULE.TIMETABLE.VIEW_AI_CREDITS_USAGE'})}
            >
              <i className='fa-solid fa-coins text-warning fs-4' />
              <span className='fw-bolder fs-6 text-warning'>
                {aiCredits?.remaining_credits !== null && aiCredits?.remaining_credits !== undefined
                  ? aiCredits.remaining_credits.toLocaleString()
                  : '0'}
              </span>
            </div>
          </div>
        )}

        {/* Theme switcher; in the profile menu below lg */}
        <div className={clsx('app-navbar-item', lgUpItemClass)}>
          <ThemeModeSwitcher toggleBtnClass={clsx('btn-active-light-primary btn-custom')} />
        </div>

        <div className={clsx('app-navbar-item', itemClass)}>
          <div
            className='cursor-pointer'
            data-kt-menu-trigger='click'
            data-kt-menu-attach='parent'
            data-kt-menu-placement='bottom-end'
          >
            <Avatar
              src={user.profilePicture}
              size='35'
              label={user.name}
              color='primary'
              circle={isSelectiveAiDomains()}
              className={clsx({'border border-primary': !!impersonation})}
            />
          </div>
          <HeaderUserMenu />
        </div>
      </div>
    </>
  )
}

export {Navbar}
